import { EntryType, type TreeNode } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import type { TransformStep, UtilsApi } from '../../types';

/**
 * The engine parses English month names only, so Dutch names are rewritten
 * first. Full names come before abbreviations they contain (oktober / okt).
 */
const DUTCH_MONTHS: [string, string][] = [
  ['januari', 'Jan'],
  ['februari', 'Feb'],
  ['maart', 'Mar'],
  ['juni', 'Jun'],
  ['juli', 'Jul'],
  ['augustus', 'Aug'],
  ['oktober', 'Oct'],
  ['mei', 'May'],
  ['mrt', 'Mar'],
  ['okt', 'Oct'],
];

const ENGLISH_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const CANONICAL_FORMAT = 'YYYY-MM-DD HH:mm';

const dutchMonthsIn = (value: string): [string, string][] => {
  let text = value.toLowerCase();
  return DUTCH_MONTHS.filter(([dutch, english]) => {
    if (!text.includes(dutch)) return false;
    text = text.split(dutch).join(english.toLowerCase());
    return true;
  });
};

export const toEnglishMonths = (value: string): string =>
  dutchMonthsIn(value).reduce(
    (text, [dutch, english]) => text.replace(new RegExp(dutch, 'gi'), english),
    value,
  );

export const buildEnglishMonthSnippet = (snippet: unknown, value: string): unknown =>
  dutchMonthsIn(value).reduce<unknown>(
    (inner, [dutch, english]) => ({ '$utils.regexp.replace': [inner, [dutch, 'gi'], english] }),
    snippet,
  );

type DateParts = { year: number; month: number; day: number; hour: number; minute: number };

const TOKEN = /(YYYY|YY|MMM|MM|DDD|DD|HH|mm)|([^A-Za-z]+)/g;

const TOKEN_PATTERNS: Record<string, string> = {
  YYYY: '(\\d{4})(?!\\d)',
  YY: '(\\d{2})(?!\\d)',
  MMM: '([A-Za-z]{3,})',
  MM: '(\\d{1,2})(?!\\d)',
  DD: '(\\d{1,2})(?!\\d)',
  HH: '(\\d{1,2})(?!\\d)',
  mm: '(\\d{2})(?!\\d)',
};

const SHORT_YEAR = '(\\d{4}|\\d{2})(?!\\d)';

const formatTokens = (format: string): string[] | null => {
  const tokens = [...format.matchAll(TOKEN)].map((m) => m[1] ?? '');
  return tokens.includes('DDD') ? null : tokens.filter(Boolean);
};

const twoDigitYear = (yy: number) => (yy > 68 ? 1900 + yy : 2000 + yy);

const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

const toParts = (tokens: string[], values: string[]): DateParts | null => {
  const parts: DateParts = { year: new Date().getFullYear(), month: 0, day: 1, hour: 0, minute: 0 };
  tokens.forEach((token, i) => {
    const raw = values[i];
    if (token === 'YYYY') parts.year = raw.length === 2 ? twoDigitYear(Number(raw)) : Number(raw);
    if (token === 'YY') parts.year = twoDigitYear(Number(raw));
    if (token === 'MM') parts.month = Number(raw);
    if (token === 'MMM') parts.month = ENGLISH_MONTHS.indexOf(raw.slice(0, 3).toLowerCase()) + 1;
    if (token === 'DD') parts.day = Number(raw);
    if (token === 'HH') parts.hour = Number(raw);
    if (token === 'mm') parts.minute = Number(raw);
  });
  const valid =
    parts.month >= 1 &&
    parts.month <= 12 &&
    parts.day >= 1 &&
    parts.day <= daysInMonth(parts.year, parts.month) &&
    parts.hour <= 23 &&
    parts.minute <= 59;
  return valid ? parts : null;
};

/**
 * Reads a date text with one of the schema's formats, the way the engine
 * does: a leading label without digits is skipped ("Date: 06-Apr-2023"),
 * any separator between the parts, trailing text allowed as long as no
 * further number follows (so a date-only format can't claim a date+time).
 * A label with digits is refused, as the engine would read those digits.
 * With `shortYear`, YYYY also takes a two-digit year ("16-Mar-22"), for
 * lists that have no YY variant of a format; the engine check decides.
 */
export const parseWithFormat = (
  sample: string,
  format: string,
  { shortYear = false } = {},
): DateParts | null => {
  const tokens = formatTokens(format);
  if (!tokens || tokens.length === 0) return null;

  const patternFor = (t: string) => (t === 'YYYY' && shortYear ? SHORT_YEAR : TOKEN_PATTERNS[t]);
  const pattern = new RegExp('^[^0-9]*?' + tokens.map(patternFor).join('[^A-Za-z0-9]*'));
  const match = toEnglishMonths(sample).match(pattern);
  if (!match) return null;
  const rest = toEnglishMonths(sample).slice(match[0].length);
  if (/^[^A-Za-z0-9]*\d/.test(rest)) return null;

  return toParts(tokens, match.slice(1));
};

const pad = (n: number) => String(n).padStart(2, '0');

export const toCanonical = ({ year, month, day, hour, minute }: DateParts) =>
  `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}`;

const specificity = (format: string) => formatTokens(format)?.length ?? 0;
const dayFirst = (format: string) => (format.startsWith('DD') ? 1 : 0);

const fitting = (samples: string[], formats: string[], shortYear: boolean) =>
  formats
    .filter(
      (format) =>
        samples.length > 0 && samples.every((s) => parseWithFormat(s, format, { shortYear })),
    )
    .sort((a, b) => specificity(b) - specificity(a) || dayFirst(b) - dayFirst(a));

/**
 * Formats from the field's list that read every sample as a valid date,
 * most specific first and day-first before year-first when both fit. Only
 * when none fits is a two-digit year read with a YYYY format.
 */
export const detectDateFormats = (samples: string[], formats: string[]): string[] => {
  const exact = fitting(samples, formats, false);
  return exact.length > 0 ? exact : fitting(samples, formats, true);
};

/** The reading detectDateFormats used: exact, else with a two-digit year. */
const readDate = (sample: string, format: string) =>
  parseWithFormat(sample, format) ?? parseWithFormat(sample, format, { shortYear: true });

const enumValues = (entry: Pick<TreeNode, 'validation'>): string[] | null => {
  const def = (entry.validation as { _def?: { typeName?: string; values?: unknown } } | undefined)?._def;
  return def?.typeName === 'ZodEnum' && Array.isArray(def.values) ? (def.values as string[]) : null;
};

export const isDateFormatField = (entry: Pick<TreeNode, 'key' | 'validation'>) =>
  /dateformat$/i.test(String(entry.key)) && enumValues(entry) !== null;

const isDateTextField = (entry: Pick<TreeNode, 'key'>) => /datereadable$/i.test(String(entry.key));

export const readableKeyFor = (formatKey: string) => formatKey.replace(/format$/i, 'readable');

export const dateSamples = (rendered: unknown): string[] => {
  const values = Array.isArray(rendered) ? rendered.flat(Infinity) : [rendered];
  const strings = values
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean);
  return [...new Set(strings)].slice(0, 10);
};

/**
 * Checks each candidate with the engine: $time.stamp with the format, then
 * back to a canonical text, which must equal the local reading. This also
 * rejects input the engine silently turns into "now".
 */
export const verifyDateFormats = async (
  samples: string[],
  formats: string[],
  callRender: UtilsApi['callRender'],
): Promise<string[]> => {
  if (formats.length === 0) return [];
  const probes: Record<string, unknown> = {};
  formats.forEach((format, f) =>
    samples.forEach((sample, s) => {
      probes[`f${f}s${s}`] = {
        '$time.format': [{ '$time.stamp': [toEnglishMonths(sample), format] }, CANONICAL_FORMAT],
      };
    }),
  );

  const result = await callRender(JSON.stringify(probes));
  if (result.isErr()) return [];
  const rendered = (result.value ?? {}) as Record<string, unknown>;

  return formats.filter((format, f) =>
    samples.every((sample, s) => {
      const parts = readDate(sample, format);
      return parts !== null && rendered[`f${f}s${s}`] === toCanonical(parts);
    }),
  );
};

const SOURCE = 'dateHandling';

const englishMonthStep: TransformStep = {
  name: 'dateTime',
  stage: 'date',
  mode: 'replace',
  apply: ({ snippet, value }, { entry }) => {
    if (!isDateTextField(entry) || typeof value !== 'string') return [];
    if (dutchMonthsIn(value).length === 0) return [];
    return [{ snippet: buildEnglishMonthSnippet(snippet, value), value: toEnglishMonths(value) }];
  },
};

const fittingFormatStep: TransformStep = {
  name: 'dateTime',
  stage: 'date',
  mode: 'replace',
  apply: () => [],
  keep: ({ entry, suggestion, suggestions }) =>
    !isDateFormatField(entry) ||
    suggestion.source === SOURCE ||
    !suggestions.some((s) => s.source === SOURCE),
};

/**
 * Date texts suggested for each `*datereadable` field, keyed by parent and
 * key. Right after loading no date text is filled in yet, so the format field
 * falls back on these. The format field declares the date text field as a
 * dependency, so the host has evaluated it by the time the format field runs.
 */
const suggestedDateTexts = new Map<string, Set<string>>();

const dateTextKey = (parentId: unknown, key: string) => `${String(parentId)}:${key.toLowerCase()}`;

const rememberDateTextStep: TransformStep = {
  name: 'dateTime',
  stage: 'date',
  mode: 'add',
  apply: ({ value }, { entry }) => {
    if (isDateTextField(entry) && typeof value === 'string' && value.trim()) {
      const key = dateTextKey(entry.parentId, String(entry.key));
      const texts = suggestedDateTexts.get(key) ?? new Set<string>();
      texts.add(value.trim());
      suggestedDateTexts.set(key, texts);
    }
    return [];
  },
};

export const forgetSuggestedDateTexts = () => suggestedDateTexts.clear();

/**
 * What was suggested for the date text field, keeping the texts that read as
 * a date with one of the formats.
 */
export const suggestedDateTextsFor = (
  parentId: unknown,
  readableKey: string,
  formats: string[],
): string[] =>
  [...(suggestedDateTexts.get(dateTextKey(parentId, readableKey)) ?? [])].filter(
    (text) => detectDateFormats([text], formats).length > 0,
  );

/**
 * The date texts to pick a format for: what the date text field holds. When
 * it is empty, or holds a text no format reads (like a time cut off at its
 * colon, "3 okt 2026 14"), what was suggested for it instead; the host
 * evaluated that field first (see fieldDependencies).
 */
export const samplesForFormat = (
  filled: string[],
  parentId: unknown,
  readableKey: string,
  formats: string[],
): string[] =>
  detectDateFormats(filled, formats).length > 0
    ? filled
    : suggestedDateTextsFor(parentId, readableKey, formats);

/** A date format field needs the date text field next to it. */
export const dateFieldDependencies = (entry: Pick<TreeNode, 'key' | 'validation'>) =>
  isDateFormatField(entry) ? [readableKeyFor(String(entry.key))] : [];

export const dateSteps: TransformStep[] = [
  rememberDateTextStep,
  englishMonthStep,
  fittingFormatStep,
];

export const dateHandling = definePlugin<{}>({
  name: 'Date & Time',
  version: '0.1.0',
  description:
    'Replaces the Date & Time transformer: picks the date format that fits the date text, checked by rendering, and translates Dutch month names.',
  initialState: {},

  bootstrap: () => forgetSuggestedDateTexts(),

  async fieldSuggestions({ entry, renderSibling, callRender }) {
    if (!isDateFormatField(entry) || !renderSibling) return [];

    const formats = enumValues(entry) ?? [];
    const readableKey = readableKeyFor(String(entry.key));
    const rendered = await renderSibling(readableKey);
    const filled = rendered.isOk() ? dateSamples(rendered.value) : [];
    const samples = samplesForFormat(filled, entry.parentId, readableKey, formats);

    const candidates = detectDateFormats(samples, formats);
    const verified = await verifyDateFormats(samples, candidates, callRender);

    return verified.map((format, i) => ({
      value: format,
      snippet: format,
      type: EntryType.Simple,
      autofill: i === 0,
      verified: true,
      expectedValue: samples[0],
    }));
  },

  transformSteps: () => dateSteps,

  fieldDependencies: ({ entry }) => dateFieldDependencies(entry),
});
